import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { Button } from 'Button';
import { Input } from 'Input';
import { Text } from 'Text';
import { useUpdateAccount } from '../useUpdateAccount';
import { type AccountFormValues } from '../editAccount.types';

const MAX_LENGTH = 255;

type Props = {
    isMobile: boolean;
    userCanHavePassword: boolean;
    onOpenChangePassword: () => void;
};

// Name + nickname + email form. Saves via AuthClient (useUpdateAccount). Email is disabled for SAML
// users. On mobile, a "Change Password" button appears here to switch to the password form.
export function AccountForm({ isMobile, userCanHavePassword, onOpenChangePassword }: Props) {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const { updateAccount, submitting } = useUpdateAccount();

    const schema = Yup.object({
        name: Yup.string().trim().required().max(MAX_LENGTH),
        nickname: Yup.string().trim().required().max(MAX_LENGTH),
        email: Yup.string().trim().required().email().max(MAX_LENGTH),
    });

    const methods = useForm<AccountFormValues>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: currentUser.name ?? '',
            nickname: currentUser.nickname ?? '',
            email: currentUser.email ?? '',
        },
    });

    const allowEmailUpdate = !currentUser.hasSamlProvider;
    const { isValid, errors } = methods.formState;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(updateAccount)} className="flex max-w-md flex-col gap-4 py-6">
                <Text as="h2" color="black" weight="semibold" className="text-base">
                    {t('settings.edit_account.edit_info')}
                </Text>
                <Input name="name" type="text" label={t('settings.edit_account.full_name')} isInvalid={!!errors.name} />
                <Input
                    name="nickname"
                    type="text"
                    label={t('settings.edit_account.nickname')}
                    isInvalid={!!errors.nickname}
                />
                <Input
                    name="email"
                    type="email"
                    label={t('settings.edit_account.email')}
                    disabled={!allowEmailUpdate}
                    isInvalid={!!errors.email}
                />
                <Button type="submit" color="primary" height={9} fullWidth isDisabled={!isValid || submitting}>
                    {t('settings.edit_account.save_changes')}
                </Button>
                {isMobile && userCanHavePassword ? (
                    // The arrow signifies transitioning to the change-password form; it inherits the
                    // button's text color (via currentColor) so it matches the border, and flips for
                    // RTL so it points in the reading direction.
                    <Button
                        type="button"
                        variant="bordered-white"
                        color="slate-grey"
                        height={9}
                        fullWidth
                        onPress={onOpenChangePassword}
                    >
                        {t('settings.edit_account.change_password')}
                        <FontAwesomeIcon icon={faArrowRight} className="ms-2 rtl:rotate-180" />
                    </Button>
                ) : null}
            </form>
        </FormProvider>
    );
}
